import { apiClient } from '../client';
import { API_CONFIG } from '../config';

export interface SubscriptionPlan {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  price: string;
  currency: string;
  billing_period: string;
  features: any;
  type?: string | null;
  is_active: boolean;
  sort_order: number;
  trial_days?: number | null;
  razorpay_plan_id?: string | null;
  cashfree_plan_id?: string | null;
}

export interface GatewayConfig {
  gateways: Array<{ name: string; label: string }>;
  default: string;
}

export interface Subscription {
  id: string;
  organization_id: string;
  plan_id: string;
  status: string;
  user_type?: string | null;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  cancelled_at: string | null;
  trial_start?: string | null;
  trial_end?: string | null;
  trial_days?: number | null;
  razorpay_subscription_id?: string | null;
  plan?: SubscriptionPlan;
  entitlements?: {
    organizationId: string;
    planId: string | null;
    planName: string;
    subscriptionId: string | null;
    subscriptionStatus: string;
    entitlements: {
      limits: Record<string, number>;
      features: Record<string, boolean>;
      sub_features?: Record<string, Record<string, number>>;
    };
    overrides: Array<{
      key: string;
      value: any;
      type: string;
      reason?: string;
    }>;
  };
}

export interface Payment {
  id: string;
  organization_id: string;
  subscription_id: string | null;
  razorpay_payment_id: string;
  razorpay_invoice_id?: string | null;
  amount: string;
  currency: string;
  status: string;
  payment_method: string | null;
  description: string | null;
  paid_at: string | null;
  created_at: string;
  plan_name?: string;
  plan_billing_period?: string;
  plan_period_start?: string;
  plan_period_end?: string;
}

export interface Addon {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  billing_period: string;
  duration_days: number;
  features: any;
  is_active: boolean;
  razorpay_plan_id?: string;
}

export interface AddonSubscription {
  id: string;
  addon_id: string;
  organization_id: string;
  status: string;
  current_period_end: string;
  addon: Addon;
}

export interface UsageStats {
  contacts: number;
  messages: number;
  agents: number;
  templates: number;
  campaigns?: number;
  automations?: number;
  whatsapp_flows?: number;
  business_accounts?: number;
  contact_tags?: number;
  inbox?: number;
  commerce?: number;
  integrations?: number;
  connected_integrations?: string[];
  admins?: number;
  users?: number;
}

export const subscriptionsApi = {
  /**
   * Get available payment gateways
   */
  async getGatewayConfig(): Promise<{ data: GatewayConfig | null; error: string | null }> {
    try {
      const response = await apiClient.get<GatewayConfig>('/subscriptions/gateway-config');
      if (response.error || !response.data) {
        return { data: null, error: response.error || 'Failed to fetch gateway config' };
      }
      return { data: response.data, error: null };
    } catch (error: any) {
      return { data: null, error: error.message || 'Failed to fetch gateway config' };
    }
  },

  /**
   * Get all available subscription plans
   */
  async getPlans(): Promise<{ data: SubscriptionPlan[] | null; error: string | null }> {
    try {
      const response = await apiClient.get<SubscriptionPlan[]>('/subscriptions/plans');

      // Check if response has error
      if (response.error) {
        return {
          data: null,
          error: response.error,
        };
      }

      // Check if data exists
      if (!response.data) {
        return {
          data: null,
          error: 'No plans data received',
        };
      }

      // Handle array response
      const plans = Array.isArray(response.data) ? response.data : [];

      return {
        data: plans,
        error: null,
      };
    } catch (error: any) {
      return {
        data: null,
        error: error.message || 'Failed to fetch subscription plans',
      };
    }
  },

  /**
   * Get current subscription for the organization
   */
  async getCurrentSubscription(): Promise<{ data: Subscription | null; error: string | null }> {
    try {
      const response = await apiClient.get<Subscription | null>('/subscriptions/current');

      if (response.error) {
        return {
          data: null,
          error: response.error,
        };
      }

      return {
        data: response.data ?? null,
        error: null,
      };
    } catch (error: any) {
      return {
        data: null,
        error: error.message || 'Failed to fetch current subscription',
      };
    }
  },

  /**
   * Get payment history for the organization
   */
  async getPaymentHistory(params?: { limit?: number; offset?: number }): Promise<{
    data: Payment[] | null;
    pagination: { limit: number; offset: number; total: number; has_more: boolean; next_offset: number | null } | null;
    error: string | null;
  }> {
    try {
      const response = await apiClient.get<Payment[]>('/subscriptions/payments', {
        params: {
          limit: params?.limit || 10,
          offset: params?.offset || 0,
        },
      });

      if (response.error || !response.data) {
        return {
          data: null,
          pagination: null,
          error: response.error || 'Failed to fetch payment history',
        };
      }

      return {
        data: Array.isArray(response.data) ? response.data : [],
        pagination: response.pagination || null,
        error: null,
      };
    } catch (error: any) {
      return {
        data: null,
        pagination: null,
        error: error.message || 'Failed to fetch payment history',
      };
    }
  },

  /**
   * Create checkout session for a new subscription
   */
  async createCheckoutSession(planId: string, gateway?: string): Promise<{ data: any | null; error: string | null; code?: string }> {
    try {
      const body: any = { planId };
      if (gateway) body.gateway = gateway;

      const response = await apiClient.post<any>('/subscriptions/create', body);

      if (response.error || !response.data) {
        // Pass the backend machine `code` through so the friendly-error catalog
        // can map it (see web/src/lib/errors/errorCatalog.ts). `error` holds the
        // human message; `code` is e.g. PAYMENT_PLAN_NOT_CONFIGURED.
        return {
          data: null,
          error: response.error || 'Failed to create subscription',
          code: (response as any).code,
        };
      }

      // Razorpay subscription returns a short_url or we need to redirect to payment
      // For now, we'll return the subscription data and handle redirect on frontend
      return {
        data: response.data,
        error: null,
      };
    } catch (error: any) {
      return {
        data: null,
        error: error.message || 'Failed to create subscription',
      };
    }
  },

  /**
   * Get current usage statistics
   */
  async getUsage(): Promise<{ data: UsageStats | null; error: string | null }> {
    try {
      const response = await apiClient.get<UsageStats>('/subscriptions/usage');

      if (response.error || !response.data) {
        return {
          data: null,
          error: response.error || 'Failed to fetch usage statistics',
        };
      }

      return {
        data: response.data,
        error: null,
      };
    } catch (error: any) {
      return {
        data: null,
        error: error.message || 'Failed to fetch usage statistics',
      };
    }
  },

  /**
   * Verify payment after checkout completion
   */
  async verifyPayment(params: { gateway?: string; razorpaySubscriptionId?: string; razorpayPaymentId?: string; razorpaySignature?: string; cashfreeOrderId?: string; cashfreePaymentId?: string }): Promise<{ success: boolean; error: string | null }> {
    try {
      const response = await apiClient.post<any>('/subscriptions/verify', params);
      return { success: !response.error, error: response.error || null };
    } catch (error: any) {
      return { success: false, error: error.message || 'Verification failed' };
    }
  },

  /**
   * Check subscription status by ID (used for polling after Cashfree redirect)
   */
  async checkSubscriptionStatus(subscriptionId: string): Promise<{ data: { id: string; status: string } | null; error: string | null }> {
    try {
      const response = await apiClient.get<{ id: string; status: string }>(`/subscriptions/status/${subscriptionId}`);
      return { data: response.data ?? null, error: response.error || null };
    } catch (error: any) {
      return { data: null, error: error.message || 'Failed to check status' };
    }
  },

  /**
   * Cancel current subscription
   */
  async cancelSubscription(): Promise<{ success: boolean; error: string | null }> {
    try {
      const response = await apiClient.post<{ message: string }>('/subscriptions/cancel');

      if (response.error || !response.success) {
        return {
          success: false,
          error: response.error || 'Failed to cancel subscription',
        };
      }

      return {
        success: true,
        error: null,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to cancel subscription',
      };
    }
  },
  /**
   * Download invoice for a payment
   */
  async downloadInvoice(paymentId: string, invoiceId?: string): Promise<{ success: boolean; error: string | null }> {
    try {
      const token = localStorage.getItem('auth_token');

      if (!token) {
        return { success: false, error: 'Authentication token not found' };
      }

      // Use a known base URL since we can't easily access it from apiClient
      // Assuming existing API calls work, /api/v1 is likely correct relative to domain
      const response = await fetch(`${API_CONFIG.BASE_URL}/subscriptions/invoice/${paymentId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        return { success: false, error: 'Failed to download invoice' };
      }

      const blob = await response.blob();

      // Create blob URL
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceId || paymentId}.pdf`);
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      window.URL.revokeObjectURL(url);

      return { success: true, error: null };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to download invoice',
      };
    }
  },

  /**
   * Get all available add-ons
   */
  async getAvailableAddons(): Promise<{ data: Addon[] | null; error: string | null }> {
    try {
      const response = await apiClient.get<Addon[]>('/addons');
      if (response.error || !response.data) {
        return { data: null, error: response.error || 'Failed to fetch addons' };
      }
      return { data: response.data, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  },

  /**
   * Get active add-on subscriptions
   */
  async getActiveAddons(): Promise<{ data: AddonSubscription[] | null; error: string | null }> {
    try {
      const response = await apiClient.get<AddonSubscription[]>('/addons/active');
      if (response.error || !response.data) {
        return { data: null, error: response.error || 'Failed to fetch active addons' };
      }
      return { data: response.data, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  },

  /**
   * Subscribe to / purchase an add-on. Hits the real purchase endpoint
   * (POST /addons/purchase with { addon_id }) — the `/addons/:id/subscribe`
   * path does not exist on the backend. Optionally routes through a specific
   * payment gateway so a Cashfree-only org isn't handed a Razorpay order.
   */
  async subscribeToAddon(addonId: string, gateway?: string): Promise<{ data: any; error: string | null; code?: string }> {
    try {
      const body: { addon_id: string; gateway?: string } = { addon_id: addonId };
      if (gateway) body.gateway = gateway;
      const response = await apiClient.post<any>('/addons/purchase', body);
      if (response.error || !response.data) {
        return { data: null, error: response.error || 'Failed to subscribe to addon', code: (response as any).code };
      }
      return { data: response.data, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  },
};
